import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Radio,
  RadioGroup,
  Skeleton,
  StyledEngineProvider,
  TextField,
  ThemeProvider,
  Tooltip,
  createTheme,
  debounce,
  useTheme,
} from '@mui/material';
import React, { Suspense, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { IOrderLicenseState } from '@AMIEWEB/Order/Store/Order.redux';

const useStyles = makeStyles()(theme => ({
  root: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    height: '15%',
    maxHeight: 250,
    overflow: 'auto',
    paddingTop: 10,
    paddingBottom: 0,
  },

  wrapper: {
    backgroundColor: '#FFFFFF !important',
    maxHeight: 300,
  },

  item: {
    borderBottom: '1px solid #EFEFEF',
    borderColor: '#f4f5f738',
    maxHeight: '2rem',
  },

  icon: {
    borderRadius: 3,
    padding: 2,
    border: '1px solid #cccccc',
    marginTop: 6,
  },
  defaultBackgroundColor: {
    backgroundColor: theme.palette.framework.system.white,
  },
  selectBackgroundColor: {
    backgroundColor: theme.palette.framework.system.skyBlue,
    border: `1px solid ${theme.palette.framework.system.white}`,
    ' svg': {
      fill: `${theme.palette.framework.system.white} !important`,
    },
  },

  iconSize: {
    fontSize: 'larger',
    padding: 4,
  },
  listItemContent: {
    display: '-webkit-box',
    maxWidth: '50ch',
    fontSize: 14,
    lineHeight: 1.4,
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    left: '-1.5rem',
    position: 'relative',
  },
  tooltip: {
    backgroundColor: theme.palette.components.tooltip.backgroundColor,
    color: '#333333',
    opacity: 1,
    textAlign: 'left',
    letterSpacing: '0px',
    boxShadow: '0px 3px 6px #00000029',
    border: '1px solid #8C8C8C',
  },
  arrow: {
    color: theme.palette.components.tooltip.arrowColor,
    opacity: 1,
    '&::before': {
      boxShadow: '0px 3px 6px #00000029',
      border: '1px solid #8C8C8C',
    },
  },
  placementBottom: {
    marginBottom: 0,
  },

  textField: {
    fontSize: 12,
    marginLeft: 12,
    marginRight: 12,
    color: '#52429A',
    '&::placeholder': {
      opacity: 1,
      fontSize: 12,
      color: '#C0C0C0',
    },
  },
  checkboxItem: {
    paddingLeft: '10px',
  },
}));

export const CustomizedLicenseStateTypeAhead = ({
  options,
  selectedValues,
  setSelectedStates,
  showDefaultIsRequired = true,
}: {
  options: any[];
  selectedValues: any[];
  setSelectedStates: (e) => void;
  showDefaultIsRequired?: boolean;
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [stateList, setStateList] = useState<any[]>([]);
  const theme = useTheme();
  const smallTextFieldTheme = createTheme(theme, {
    components: {
      MuiFormControl: {
        styleOverrides: {
          root: {
            height: '37.625px',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: { height: '37.625px' },
        },
      },
    },
  });

  const handleToggle = value => {
    const selectedStateOptions = [...selectedValues];
    const duplicateState = selectedStateOptions?.find(item => item?.state?.id === value?.id);
    if (duplicateState) {
      const filteredStateOptions = selectedStateOptions?.filter(item => item?.state?.id !== value?.id);
      setSelectedStates(filteredStateOptions);
    } else {
      const selectedValue: IOrderLicenseState = {
        state: value,
        isRequired: showDefaultIsRequired,
        documentAttachmentRequired: false,
      };
      selectedStateOptions.push(selectedValue);
      setSelectedStates(selectedStateOptions);
    }
  };

  const handleDocumentRequired = value => {
    const updatedStateData = selectedValues?.map(item => {
      if (item?.state?.id === value?.id) {
        item.documentAttachmentRequired = !item.documentAttachmentRequired;
      }
      return item;
    });
    setSelectedStates(updatedStateData);
  };

  const handleRequirementChange = (stateValue, value) => {
    const existingStateValue = selectedValues?.find(item => item?.state?.id === stateValue?.id);
    let updatedStateOption = {};
    if (value === 'req') {
      updatedStateOption = {
        ...existingStateValue,
        isRequired: true,
      };
    } else {
      updatedStateOption = {
        ...existingStateValue,
        isRequired: false,
      };
    }
    const selectedStateOptions = [...selectedValues];
    const updatedStateOptionArray = selectedStateOptions?.map(item => {
      if (item?.state?.id === existingStateValue?.state?.id) {
        return updatedStateOption;
      }
      return item;
    });
    setSelectedStates(updatedStateOptionArray);
  };

  const handleSearch = event => {
    (() => {
      if (options?.length > 0) {
        const filteredStates = options?.filter(item =>
          item?.name?.toLowerCase()?.includes(event.target.value?.toLowerCase()),
        );
        setStateList(filteredStates);
      }
    })();
  };

  useEffect(() => {
    setStateList(options);
  }, [options]);

  const debouncedSearchHandler = debounce(handleSearch, 300);

  const OptionRender = React.useMemo(() => {
    return (
      <List className={classes.root} id={'stateCell'}>
        <Suspense fallback={<Skeleton variant="rectangular" />}>
          {(stateList || [])?.map(stateValue => {
            const selectedState = selectedValues?.find(item => item?.state?.id === stateValue?.id);
            return (
              <ListItem key={stateValue?.id} role={undefined} dense className={classes.item}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    checked={!!selectedState}
                    tabIndex={-1}
                    inputProps={{ 'aria-labelledby': stateValue?.name }}
                    color="primary"
                    onChange={e => handleToggle(stateValue)}
                    className={classes.checkboxItem}
                  />
                </ListItemIcon>
                <ListItemText
                  classes={{
                    primary: classes.listItemContent,
                  }}
                  id={stateValue?.id}
                  primary={stateValue?.name}
                />
                <ListItemSecondaryAction>
                  {!!selectedState &&
                    <Grid container>
                      <Grid item>
                        <FormGroup row>
                          <FormControlLabel
                            control={
                              <Checkbox
                                disabled
                                color="primary"
                                checked={true} // "isRequired" always true
                              />
                            }
                            label="Required"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="primary"
                                checked={selectedState?.documentAttachmentRequired}
                                onChange={() => handleDocumentRequired(stateValue)}
                              />
                            }
                            label="Attach Doc."
                          />
                        </FormGroup>
                      </Grid>
                    </Grid>
                  }
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </Suspense>
      </List>
    );
  }, [selectedValues, stateList]);

  return (
    <>
      <Box className={classes.wrapper}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={smallTextFieldTheme}>
            <TextField
              variant="outlined"
              color="primary"
              placeholder={t('appHeader.searchPlaceHolder')}
              id="txtLicense"
              autoComplete="on"
              onChange={debouncedSearchHandler}
              fullWidth
              style={{ margin: '0px 15px', width: '92%' }}
              size={'small'}
              InputProps={{
                classes: { input: classes.textField },
                startAdornment: <SearchIcon style={{ opacity: 0.6 }} />,
                'aria-label': 'filter-type-ahead',
              }}
            />
          </ThemeProvider>
        </StyledEngineProvider>
        {OptionRender}
      </Box>
    </>
  );
};
